import { css } from '@emotion/react';
import { PageMode } from '$/components/ui/Input/PageBuilder.types';
import { WithUploadImage } from '$/components/WithUploadImage/WithUploadImage';
import {
  MediaHint,
  MediaRef,
  ProductHuntMedia,
  ProductHuntPromoInteractionDetails,
} from '$/GraphQL/schema/schema';
import { CloseIcon, PlusIcon, Spinner } from '$/images/Icons';
import { readableColorOnBackground } from '$/utils/colors';
import { langDirClassname } from '$/shared/frontend/utils/i18n';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import tw from 'twin.macro';
import { MediaRibbon } from '../cards/MediaRibbon';
import { InteractionInfo } from '../InteractionInfo';
import { InteractionModalTitleProps } from '../types';
import { EditableInput } from '$/components/ui/EditableInput';
import { staticWebinarDimensions } from '$/components/WebinarPlayer/context';
import { useInteractionIconColor } from '$/hooks/useInteractionIconColor';
import { Portal } from '$/components/Portal';
import { useImageSrcFromRef } from '$/hooks/useImageSrcFromRef';
import { Popup } from '$/components/ui/Popup';
import Modal from '$/components/ui/Modal';
import Form from '$/components/ui/Form';
import Input from '$/components/ui/Input/Basics/Input';
import { RatioOfPreviewCard } from './variables';

interface ImageButtonProps {
  value?: MediaRef | null;
  interaction?: InteractionInfo;
  onChange?: (ref: MediaRef | null | undefined) => void;
  disabled?: boolean;
  className?: string;
  fit?: 'cover' | 'contain';
  showFullImage?: boolean;
  hideRecommended?: boolean;
}

const ImageButton: React.FC<ImageButtonProps> = ({
  interaction,
  hideRecommended,
  fit = 'cover',
  showFullImage,
  value,
  ...props
}) => {
  const dir = langDirClassname(interaction?.ewebinar?.viewerRoomSettings.language);

  const onChangeImage = React.useCallback(
    (val: MediaRef | null | undefined) => {
      props.onChange && props.onChange(val);
    },
    [props]
  );

  const width = staticWebinarDimensions.actionsPane.icard.widthRem * 14;
  const height = width * staticWebinarDimensions.actionsPane.icard.mediaTitleRatio;
  const Icon = interaction?.icon();

  return (
    <div
      className={`${props.className} ${props.disabled && 'disabled'}`}
      dir='ltr'
      css={css`
        &:hover {
          > * {
            cursor: pointer;
          }
        }
        &.disabled {
          pointer-events: none;
          ${tw`opacity-disabled`}
        }
      `}
    >
      <WithUploadImage
        value={value}
        name='details.imageMediaRef'
        onChange={onChangeImage}
        asset={{
          entity: 'ewebinar',
          entityId: interaction?.ewebinar?.id,
          name: `interaction-${interaction?.id ?? 'new'}`,
          container: {
            width: width,
            height: showFullImage ? undefined : height,
            hint: MediaHint.Card,
          },
          fit,
          position: 'top center',
          hideRecommended: hideRecommended,
        }}
        interactionBar={
          interaction && Icon
            ? {
                text: interaction.details?.title,
                icon: (
                  <Icon
                    className='w-1_125e h-1_125e'
                    css={css`
                      transform: scale(${RatioOfPreviewCard});
                    `}
                  />
                ),
              }
            : undefined
        }
      >
        {({ loading, value, onRemove }): JSX.Element => {
          return (
            <div
              className={`flex font-body bg-gray-700 text-white text-sm rounded-xl p-2 items-center `}
              css={css`
                .image-icon {
                  width: 18px !important;
                  height: 18px !important;
                }
                position: absolute;
                ${dir === 'rtl'
                  ? `
                    right: 0.5rem;
                  `
                  : `
                    left: 0.5rem;
                  `}
                top: -1.3rem;
                height: 30px;
                z-index: 1;
              `}
            >
              <Popup
                className='ml-0'
                onClick={(): void => {
                  if (!value?.imageUrl) return;
                }}
                content={<span>{value?.imageUrl ? 'Edit image' : 'Add an image'}</span>}
                trigger={<span>{value?.imageUrl ? 'Edit image' : 'Add image'}</span>}
              />
              <Popup
                className='ml-0'
                onClick={(): void => {
                  if (!value?.imageUrl) return;
                }}
                content={<span>{value?.imageUrl ? 'Remove image' : 'Add an image'}</span>}
                trigger={
                  <span>
                    {loading ? (
                      <Spinner className='relative text-white image-icon ml-2' />
                    ) : (
                      <div
                        className={`ml-2 button-control ${
                          !value?.imageUrl ? 'bg-white text-gray-0' : 'bg-coral-1 text-white'
                        } image-icon rounded-full  flex items-center justify-center text-md`}
                        onClick={(e): void => {
                          if (!value?.imageUrl) return;
                          onRemove();
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        {!value?.imageUrl ? (
                          <PlusIcon className='h-1e' />
                        ) : (
                          <CloseIcon className='w-1/2e h-1/2e' />
                        )}
                      </div>
                    )}
                  </span>
                }
              />
            </div>
          );
        }}
      </WithUploadImage>
    </div>
  );
};

export const InteractionModalTitle: React.FC<InteractionModalTitleProps> = ({
  interaction,
  state,
  className,
  disableEdit,
  mentions,
}) => {
  const { watch } = useFormContext();

  const ewebinar = interaction.ewebinar!;

  const Icon = interaction.icon();
  const imageMediaRef = watch('details.imageMediaRef') as MediaRef | null | undefined;
  const [dimensions, setDimensions] = useState([0, 0]);
  const dir = langDirClassname(ewebinar.viewerRoomSettings?.language);

  const rootRef = React.useCallback((node: HTMLDivElement) => {
    if (!node) return;
    setDimensions([node.clientWidth, node.clientHeight]);
  }, []);

  const media =
    (
      (interaction.fragment.details?.integration as unknown as ProductHuntPromoInteractionDetails)
        ?.post?.media as ProductHuntMedia[] | undefined
    )?.filter((m) => m.type === 'image') ?? [];

  const width = staticWebinarDimensions.actionsPane.icard.widthRem * 14;
  const height = width * staticWebinarDimensions.actionsPane.icard.mediaTitleRatio;
  const titleImageUrl = useImageSrcFromRef(imageMediaRef, {
    w: width,
    h: height,
  });
  const hasMedia = Boolean(media.length > 0 || imageMediaRef || interaction.requiresHeaderMedia());

  const { css: iconColorCss } = useInteractionIconColor(interaction, ewebinar, {
    inverted: hasMedia,
  });

  return (
    <Modal.Title
      ref={rootRef}
      className={`relative text-white leading-tight transition-all duration-200 mb-1 ${className}`}
      css={css`
        width: 375px;
        background-color: #fff;
        padding: calc(1rem * ${RatioOfPreviewCard});
        font-size: unset;

        ${tw`font-semibold`}
        &::after {
          content: '';
          height: 1px;
          width: 100%;
          background: #e5e5e5;
          position: absolute;
          left: 0;
          bottom: 7px;
        }
        .icard-title-area {
          width: 100%;

          .icard-title {
            div,
            & input,
            & .EditableInput {
              ${state.mode !== PageMode.edit
                ? `
                border-color: transparent !important;
                background: transparent !important;
                opacity: 1 !important;
                color: #fff !important;
              `
                : ``}
            }
          }
        }
        .icon {
          transform: scale(${RatioOfPreviewCard});

          ${iconColorCss}
          ${dir === 'ltr'
            ? `
              margin-right: 0.25rem;
              margin-left: 0.75rem;
            `
            : `
              margin-left: 0.25rem;
              margin-right: 0.75rem;
            `}
        }
        ${hasMedia
          ? `
            align-items: start;
            ${media.length > 0 ? '' : `background-image: url(${titleImageUrl});`}
            background-size: cover;
            background-repeat: no-repeat;
            background-position: top center;
            padding-bottom: 0;
            padding-top: ${staticWebinarDimensions.actionsPane.icard.mediaTitleRatio * 100}%;

            .icard-title-area {
              position: absolute;
              bottom: 0;
              left: 0;
              right: 0;
              min-height: 2rem;
              background: rgba(0, 0, 0, 0.4);
              backdrop-filter: blur(2px);
              z-index: 10;
              padding: 0.6rem 1.5rem;
            }
            `
          : `
            align-items: center;
          `}
      `}
    >
      <div className='icard-title-area flex w-full items-center'>
        <div className='icard-title flex flex-1'>
          <Form.Field
            name='details.title'
            className='title w-full mb-0 transition-colors duration-100 relative'
            component={
              <EditableInput
                withBorder
                id='interactionTitle'
                css={css`
                  border-width: 2px;
                  border-style: dashed;
                  height: auto;
                  transition: background;
                  max-height: 47px;
                  padding: 0.25rem 1rem !important;
                  ${tw`duration-100 overflow-y-auto w-full leading-tight focus:outline-none`}
                  color: ${imageMediaRef ? 'white' : readableColorOnBackground('#fff')} !important;
                  &.has-focus {
                    color: ${imageMediaRef
                      ? 'white'
                      : readableColorOnBackground('#fff')} !important;
                  }
                  &.is-placeholder {
                    opacity: 1;
                    ${tw`text-color-input-placeholder`}
                  }
                  &.is-disabled {
                    opacity: 0.45;
                  }
                `}
                mentions={mentions}
                placeholder='Card title'
                autoFocus
                noFormatting
                disabled={state.mode !== PageMode.edit}
                textDirectionality={dir === 'rtl' ? 'RTL' : 'LTR'}
              />
            }
          />
        </div>
        <div className='flex icon'>
          <Icon
            css={css`
              height: 1.125rem;
              width: 1.125rem;
            `}
          />
        </div>
      </div>

      {interaction?.type !== 'ProductHunt-Promo' && (
        <Portal containerId='add-image-button'>
          <Form.Field
            name='details.imageMediaRef'
            component={<ImageButton disabled={disableEdit} interaction={interaction} />}
            className='mb-0 w-auto'
          />
          <Form.Field name='giphyType' type='hidden' component={<Input />} />
        </Portal>
      )}

      <MediaRibbon media={media} width={dimensions[0]} height={dimensions[1]} />
    </Modal.Title>
  );
};
